export default {
    /**
     * Check if a font is available
     * @param {string} fontName - The font name to check
     * @returns {boolean} - Whether the font is loaded
     */
    isFontLoaded(fontName) {
        return document.fonts.check(`12pt "${fontName}"`);
    },

    /**
     * Get all loaded fonts matching a font family name
     * @param {string} fontFamily - The font family name
     * @returns {Array<FontFace>} - Array of matching FontFace objects
     */
    getFontsByFamily(fontFamily) {
        return Array.from(document.fonts.values()).filter(
            (font) => font.family === fontFamily
        );
    },

    /**
     * Load a font dynamically if needed
     * @param {string} fontFamily - The font family name
     * @param {string} fontUrl - URL to the font file
     * @returns {Promise<FontFace>} - The loaded font face
     */
    async loadFont(fontFamily, fontUrl, descriptors = {}) {
        const font = new FontFace(fontFamily, `url(${fontUrl})`, descriptors);
        const loadedFont = await font.load();
        document.fonts.add(loadedFont);
        return loadedFont;
    },

    // Updated cache management with localStorage persistence
    _fontWeightCache: null,

    /**
     * Build a complete mapping of font-family names to weights
     * @returns {Object} - Map of font family names to their weights
     */
    _buildFontWeightMapping() {
        // Try to load from localStorage first
        try {
            const cachedMapping = localStorage.getItem("fontWeightCache");
            if (cachedMapping) {
                return JSON.parse(cachedMapping);
            }
        } catch (e) {
            console.warn("Could not load font cache from localStorage", e);
        }

        // If no cache exists, build it
        const mapping = {};

        try {
            // Process all stylesheets we can access
            for (const stylesheet of document.styleSheets) {
                try {
                    const cssRules = stylesheet.cssRules || stylesheet.rules;

                    for (const rule of cssRules) {
                        if (rule.type === CSSRule.FONT_FACE_RULE) {
                            const fontFamily = rule.style
                                .getPropertyValue("font-family")
                                .replace(/["']/g, "");
                            const fontWeight =
                                rule.style.getPropertyValue("font-weight");

                            if (fontFamily && fontWeight) {
                                mapping[fontFamily] = fontWeight;
                            }
                        }
                    }
                } catch (e) {
                    // Skip inaccessible stylesheets
                }
            }

            // Store in localStorage for future use
            try {
                localStorage.setItem(
                    "fontWeightCache",
                    JSON.stringify(mapping)
                );
            } catch (e) {
                console.warn("Could not save font cache to localStorage", e);
            }
        } catch (e) {
            console.warn("Error building font weight mapping", e);
        }

        return mapping;
    },

    /**
     * Get the font-weight from CSS for a given font family (performant, uses caching)
     * @param {string} fontFamily - The font family name
     * @returns {string|number|null} - The font weight or null if not found
     */
    getFontWeightFromCSS(fontFamily) {
        // Clean the font name
        const cleanFontName = fontFamily.replace(/["']/g, "");

        // Initialize cache if needed
        if (!this._fontWeightCache) {
            this._fontWeightCache = this._buildFontWeightMapping();
        }

        // Return from cache
        return this._fontWeightCache[cleanFontName] || null;
    },

    /**
     * Force refresh the font cache and update localStorage
     */
    refreshFontWeightCache() {
        // Clear localStorage cache
        try {
            localStorage.removeItem("fontWeightCache");
        } catch (e) {
            console.warn("Could not clear font cache from localStorage", e);
        }

        // Rebuild cache
        this._fontWeightCache = this._buildFontWeightMapping();
    }
};
